import {
  Alert,
  Box,
  Button,
  CssBaseline,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import React, { useEffect, useMemo, useState } from "react";
import Topbar from "../../../../components/topbar/TopBar";
import { getCancellationReasonListUseCase } from "../../reason/domain/usecases/getCancellationReasonListUseCase";
import { createBookingCancellationUseCase } from "../domain/usecases/createBookingCancellationUseCase";

const CANCELLED_BY_OPTIONS = [
  { value: "CLIENT", label: "CLIENT" },
  { value: "DRIVER", label: "DRIVER" },
  { value: "ENTERPRISE", label: "ENTEPRISE" },
  { value: "SYSTEM", label: "SYSTEM" },
];

function createInitialForm() {
  return {
    bookingId: "",
    cancelledByType: "CLIENT",
    cancelledById: "",
    cancellationReasonId: "",
    bookingStatusBefore: "",
    cancellationFee: "0",
  };
}

function createPayload(form) {
  return {
    booking_id: Number(form.bookingId),
    cancelled_by_type: form.cancelledByType,
    cancelled_by_id: form.cancelledById === "" ? null : Number(form.cancelledById),
    cancellation_reason_id: Number(form.cancellationReasonId),
    booking_status_before:
      form.bookingStatusBefore.trim() === ""
        ? null
        : form.bookingStatusBefore.trim(),
    cancellation_fee:
      form.cancellationFee === "" ? 0 : Number(form.cancellationFee),
  };
}

function isFormReady(form) {
  return (
    Number.isInteger(Number(form.bookingId)) &&
    Number(form.bookingId) > 0 &&
    CANCELLED_BY_OPTIONS.some(
      (option) => option.value === form.cancelledByType,
    ) &&
    (form.cancelledById === "" ||
      (Number.isInteger(Number(form.cancelledById)) &&
        Number(form.cancelledById) > 0)) &&
    Number.isInteger(Number(form.cancellationReasonId)) &&
    Number(form.cancellationReasonId) > 0 &&
    form.bookingStatusBefore.trim().length <= 50 &&
    form.cancellationFee !== "" &&
    Number.isFinite(Number(form.cancellationFee)) &&
    Number(form.cancellationFee) >= 0
  );
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return response?.success === true || message.includes("success");
}

export const CancellationBookingPage = () => {
  const [form, setForm] = useState(createInitialForm());
  const [reasons, setReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const canSubmit = useMemo(() => isFormReady(form), [form]);

  useEffect(() => {
    const loadReasons = async () => {
      try {
        setLoadingReasons(true);
        const records = await getCancellationReasonListUseCase();
        const activeReasons = records.filter((reason) => reason.active !== false);
        setReasons(activeReasons);
        if (activeReasons.length > 0) {
          setForm((prev) => ({
            ...prev,
            cancellationReasonId: String(activeReasons[0].id),
          }));
        }
      } catch (error) {
        setReasons([]);
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar la lista de motivos.",
        });
      } finally {
        setLoadingReasons(false);
      }
    };

    loadReasons();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreate = async () => {
    const payload = createPayload(form);

    try {
      setSaving(true);
      const response = await createBookingCancellationUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo crear la cancelacion del booking.");
      }

      setForm({
        ...createInitialForm(),
        cancellationReasonId: reasons[0] ? String(reasons[0].id) : "",
      });
      setMessage({
        open: true,
        type: "success",
        text: "Cancelacion de booking creada correctamente.",
      });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo crear la cancelacion del booking.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 760,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{ color: "#fff !important" }}
                >
                  CREAR CANCELACION DE BOOKING
                </Typography>
              </Box>

              <Box sx={{ p: { xs: 1, md: 3 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    mx: "auto",
                    width: "100%",
                    maxWidth: 560,
                    borderRadius: 3,
                    border: "1px solid rgba(20, 83, 45, 0.12)",
                    p: { xs: 1, md: 2 },
                  }}
                >
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Booking ID"
                      name="bookingId"
                      value={form.bookingId}
                      onChange={handleChange}
                      inputProps={{ min: "1", step: "1" }}
                    />
                    <TextField
                      fullWidth
                      select
                      label="Tipo de usuario que cancela"
                      name="cancelledByType"
                      value={form.cancelledByType}
                      onChange={handleChange}
                    >
                      {CANCELLED_BY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth
                      type="number"
                      label="ID del usuario que cancela"
                      name="cancelledById"
                      value={form.cancelledById}
                      onChange={handleChange}
                      inputProps={{ min: "1", step: "1" }}
                    />
                    <TextField
                      fullWidth
                      select
                      label="Motivo de cancelacion"
                      name="cancellationReasonId"
                      value={form.cancellationReasonId}
                      onChange={handleChange}
                      disabled={loadingReasons || reasons.length === 0}
                    >
                      {reasons.length === 0 ? (
                        <MenuItem value="">
                          {loadingReasons
                            ? "Cargando motivos..."
                            : "Sin motivos registrados"}
                        </MenuItem>
                      ) : (
                        reasons.map((reason) => (
                          <MenuItem key={reason.id} value={String(reason.id)}>
                            {reason.code} - {reason.name}
                          </MenuItem>
                        ))
                      )}
                    </TextField>
                    <TextField
                      fullWidth
                      label="Estado previo del booking"
                      name="bookingStatusBefore"
                      value={form.bookingStatusBefore}
                      onChange={handleChange}
                      inputProps={{ maxLength: 50 }}
                      helperText={`${form.bookingStatusBefore.length}/50`}
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label="Penalidad de cancelacion"
                      name="cancellationFee"
                      value={form.cancellationFee}
                      onChange={handleChange}
                      inputProps={{ min: "0", step: "0.01" }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleCreate}
                      disabled={!canSubmit || saving || loadingReasons}
                      sx={{
                        mt: 1,
                        minHeight: 50,
                        borderRadius: 2,
                        textTransform: "uppercase",
                        fontWeight: 700,
                        bgcolor: "#165c53",
                        color: "#fff",
                        "&:hover": {
                          bgcolor: "#12483f",
                        },
                      }}
                    >
                      {saving ? "CREANDO..." : "CREAR"}
                    </Button>
                  </Stack>
                </Paper>
              </Box>
            </Paper>
          </Stack>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
};
